import { useMemo, useRef, useState } from 'react';
import BarAxisLabels from '@/components/feature/BarAxisLabels';
import ChartGuides from '@/components/feature/ChartGuides';
import useBarAxis from '@/hooks/useBarAxis';
import { useChartKeyboard } from '@/hooks/useChartKeyboard';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { buildTicks, formatPercentTick, isBarLabeled, niceBounds, niceCeil, selectMobileBarLabels, type YearDivider } from '@/utils/chartAxis';

interface ReturnPoint {
  month: string;
  short: string;
  axisLabel: string;
  year: number;
  pct: number;
}

interface MonthlyReturnsBarsProps {
  data: ReturnPoint[];
}

export default function MonthlyReturnsBars({ data }: MonthlyReturnsBarsProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const lineRef = useRef<HTMLDivElement | null>(null);
  const [active, setActive] = useState<number | null>(null);
  const narrow = useMediaQuery('(max-width: 640px)');

  /** Compound the monthly returns so the lower panel shows cumulative growth. */
  const cumulative = useMemo(() => {
    let acc = 1;
    return data.map((point) => {
      acc *= 1 + point.pct / 100;
      return (acc - 1) * 100;
    });
  }, [data]);

  const handleKeyDown = useChartKeyboard(data.length, setActive);

  const maxAbs = Math.max(...data.map((d) => Math.abs(d.pct)), 1);
  const scaleMax = niceCeil(maxAbs);
  const n = data.length;
  const mobileLabels = useMemo(() => new Set(selectMobileBarLabels(n, 3)), [n]);

  /* Guide positions measured from the real bar cells so the crosshair, year
     dividers and tick dots line up with the flex gap instead of drifting. */
  const { ref: barsRef, centers } = useBarAxis(n);
  const ready = centers.length === n;
  const centerAt = (i: number) => centers[i] ?? ((i + 0.5) / n) * 100;
  const yearDividers: YearDivider[] = ready
    ? data.reduce<YearDivider[]>((acc, point, i) => {
        if (i > 0 && point.year !== data[i - 1].year) {
          acc.push({ x: (centers[i - 1] + centers[i]) / 2, label: String(point.year) });
        }
        return acc;
      }, [])
    : [];
  const tickPositions = ready ? centers.filter((_, i) => isBarLabeled(i, n)) : [];

  // Top panel — monthly return, symmetric around zero.
  const barTicks = narrow
    ? [scaleMax, 0, -scaleMax]
    : [scaleMax, scaleMax / 2, 0, -scaleMax / 2, -scaleMax];
  const barPosOf = (value: number) => ((scaleMax - value) / (scaleMax * 2)) * 100;

  // Bottom panel — cumulative return on its own clean axis.
  const cumBounds = niceBounds(Math.min(0, ...cumulative), Math.max(0, ...cumulative), 2);
  const cumRange = cumBounds.top - cumBounds.bottom || 1;
  const cumPosOf = (value: number) => (1 - (value - cumBounds.bottom) / cumRange) * 100;
  const cumTicks = narrow
    ? [cumBounds.top, cumBounds.bottom]
    : buildTicks(cumBounds.bottom, cumBounds.top, cumBounds.step);
  const cumLinePoints = [
    `0,${cumPosOf(0)}`,
    ...cumulative.map((value, i) => `${(centerAt(i) / 100) * 1000},${cumPosOf(value)}`),
  ].join(' ');

  const setActiveFromClientX = (clientX: number) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) return;
    const ratio = (clientX - rect.left) / rect.width;
    const index = Math.min(n - 1, Math.max(0, Math.floor(ratio * n)));
    setActive(index);
  };

  return (
    <div className="w-full">
      {/* TOP PANEL — monthly return bars */}
      <div className="flex gap-2 md:gap-3">
        <div className="relative h-[180px] w-9 shrink-0 md:h-[200px] md:w-11">
          {barTicks.map((tick) => (
            <span
              key={tick}
              className="absolute right-0 -translate-y-1/2 whitespace-nowrap font-num text-[10px] font-medium text-foreground-400 md:text-[11px]"
              style={{ top: `${barPosOf(tick)}%` }}
            >
              {formatPercentTick(tick, tick !== 0 && Math.abs(tick) < 10 ? 1 : 0, true)}
            </span>
          ))}
        </div>

        <div className="min-w-0 flex-1">
          <div className="relative h-[180px] md:h-[200px]">
            {/* horizontal gridlines */}
            <div className="pointer-events-none absolute inset-0" aria-hidden="true">
              {barTicks.map((tick) => (
                <div
                  key={tick}
                  className={`absolute left-0 right-0 border-t ${
                    tick === 0 ? 'border-background-300' : 'border-background-200/70'
                  }`}
                  style={{ top: `${barPosOf(tick)}%` }}
                ></div>
              ))}
            </div>

            <div
              ref={containerRef}
              className="relative h-full rounded-md outline-none focus-visible:ring-2 focus-visible:ring-accent-400"
              role="img"
              aria-label="Monthly return distribution — use the left and right arrow keys to step through each month"
              tabIndex={0}
              onKeyDown={handleKeyDown}
              onMouseLeave={() => setActive(null)}
              onTouchStart={(event) => setActiveFromClientX(event.touches[0]?.clientX ?? 0)}
              onTouchMove={(event) => setActiveFromClientX(event.touches[0]?.clientX ?? 0)}
            >
              <div ref={barsRef} className="flex h-full items-stretch gap-1.5 md:gap-2">
                {data.map((point, i) => {
                  const positive = point.pct >= 0;
                  const heightPct = (Math.abs(point.pct) / scaleMax) * 46;
                  const isActive = active === i;
                  return (
                    <div
                      key={point.month}
                      className="group relative flex flex-1 cursor-pointer flex-col justify-center"
                      onMouseEnter={() => setActive(i)}
                    >
                      <div
                        className={`pointer-events-none absolute left-1/2 z-20 -translate-x-1/2 whitespace-nowrap rounded-md bg-primary-950 px-2 py-1 font-num text-[11px] font-semibold text-background-50 transition-opacity ${
                          positive ? 'top-1' : 'bottom-1'
                        } ${isActive ? 'opacity-100' : 'opacity-0'}`}
                      >
                        <span className="block">
                          {positive ? '+' : ''}
                          {point.pct.toFixed(1)}%
                        </span>
                        <span className="block text-[10px] font-normal text-accent-200">
                          cum {formatPercentTick(cumulative[i], 1, true)}
                        </span>
                      </div>
                      {/* upper half (positive) */}
                      <div className="flex flex-1 items-end">
                        {positive ? (
                          <div
                            className={`w-full rounded-t-md bg-gradient-to-t from-primary-500 to-primary-400 transition-all duration-300 ${
                              isActive ? 'opacity-80' : ''
                            }`}
                            style={{ height: `${heightPct * 2}%` }}
                          ></div>
                        ) : null}
                      </div>
                      {/* lower half (negative) */}
                      <div className="flex flex-1 items-start">
                        {!positive ? (
                          <div
                            className={`w-full rounded-b-md bg-gradient-to-b from-secondary-400 to-secondary-300 transition-all duration-300 ${
                              isActive ? 'opacity-80' : ''
                            }`}
                            style={{ height: `${heightPct * 2}%` }}
                          ></div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* year dividers + hover crosshair */}
              {ready && (
                <ChartGuides
                  yearDividers={yearDividers}
                  crosshair={active !== null ? centerAt(active) : null}
                  accent="primary"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* BOTTOM PANEL — cumulative return, shares the same month axis */}
      <div className="mt-6">
        <div className="flex gap-2 md:gap-3">
          <div className="w-9 shrink-0 md:w-11"></div>
          <p className="mb-2 min-w-0 flex-1 text-[11px] font-semibold uppercase tracking-wide text-accent-700">
            Cumulative return
          </p>
        </div>
        <div className="flex gap-2 md:gap-3">
        <div className="relative h-[110px] w-9 shrink-0 md:h-[128px] md:w-11">
          {cumTicks.map((tick) => (
            <span
              key={tick}
              className="absolute right-0 -translate-y-1/2 whitespace-nowrap font-num text-[10px] font-semibold text-accent-700 md:text-[11px]"
              style={{ top: `${cumPosOf(tick)}%` }}
            >
              {formatPercentTick(tick, Math.abs(tick) < 10 && tick !== 0 ? 1 : 0, true)}
            </span>
          ))}
        </div>

        <div className="min-w-0 flex-1">
          <div className="relative h-[110px] md:h-[128px]">
            {/* horizontal gridlines */}
            <div className="pointer-events-none absolute inset-0" aria-hidden="true">
              {cumTicks.map((tick) => (
                <div
                  key={tick}
                  className="absolute left-0 right-0 border-t border-background-200/70"
                  style={{ top: `${cumPosOf(tick)}%` }}
                ></div>
              ))}
            </div>

            <div
              ref={lineRef}
              className="relative h-full rounded-md outline-none focus-visible:ring-2 focus-visible:ring-accent-400"
              role="img"
              aria-label="Cumulative return over the same months — use the left and right arrow keys to step through each month"
              tabIndex={0}
              onKeyDown={handleKeyDown}
              onMouseLeave={() => setActive(null)}
              onTouchStart={(event) => setActiveFromClientX(event.touches[0]?.clientX ?? 0)}
              onTouchMove={(event) => setActiveFromClientX(event.touches[0]?.clientX ?? 0)}
            >
              <svg
                className="pointer-events-none absolute inset-0 h-full w-full text-accent-500"
                viewBox="0 0 1000 100"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <polyline
                  points={cumLinePoints}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
              </svg>

              {/* year dividers, month ticks, hover crosshair */}
              {ready && (
                <ChartGuides
                  yearDividers={yearDividers}
                  ticks={tickPositions}
                  crosshair={active !== null ? centerAt(active) : null}
                  ticksBelow
                  accent="primary"
                />
              )}

              {/* invisible hit areas for hover / tap */}
              <div className="absolute inset-0 flex gap-1.5 md:gap-2">
                {data.map((point, i) => (
                  <div
                    key={point.month}
                    className="h-full flex-1 cursor-pointer"
                    onMouseEnter={() => setActive(i)}
                  ></div>
                ))}
              </div>

              {/* active marker dot */}
              {active !== null ? (
                <div
                  className="pointer-events-none absolute z-20 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent-500 ring-2 ring-background-50"
                  style={{
                    left: `${centerAt(active)}%`,
                    top: `${cumPosOf(cumulative[active])}%`,
                  }}
                  aria-hidden="true"
                ></div>
              ) : null}

              {/* active cumulative value bubble */}
              {active !== null ? (
                <div
                  className="pointer-events-none absolute z-30 whitespace-nowrap rounded-md bg-primary-950 px-2 py-1 font-num text-[11px] font-semibold text-background-50"
                  style={{
                    left: `${Math.min(90, Math.max(10, centerAt(active)))}%`,
                    top: `${cumPosOf(cumulative[active])}%`,
                    transform:
                      cumPosOf(cumulative[active]) < 42
                        ? 'translate(-50%, 14px)'
                        : 'translate(-50%, calc(-100% - 14px))',
                  }}
                >
                  <span className="block text-accent-200">
                    cum {formatPercentTick(cumulative[active], 1, true)}
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* shared month axis */}
      <div className="mt-3 flex gap-2 md:gap-3">
        <div className="w-9 shrink-0 md:w-11"></div>
        <div className="min-w-0 flex-1">
          <BarAxisLabels points={data} active={active} mobileShow={mobileLabels} />
        </div>
      </div>
    </div>
  );
}